import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface Profile {
  id: string;
  user_id: string;
  display_name: string | null;
  username: string | null;
  avatar_url: string | null;
  show_earnings_to_friends: boolean;
  created_at: string;
  updated_at: string;
}

export const useProfile = () => {
  const { user, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async () => {
    if (!isAuthenticated || !user) {
      setProfile(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
      }

      if (data) {
        setProfile(data as Profile);
      } else {
        // Create a profile if it doesn't exist
        const { data: newProfile, error: createError } = await supabase
          .from('profiles')
          .insert({ user_id: user.id })
          .select()
          .single();

        if (createError) {
          console.error('Error creating profile:', createError);
        } else {
          setProfile(newProfile as Profile);
        }
      }
    } catch (err) {
      console.error('Error in useProfile:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [isAuthenticated, user?.id]);

  const updateDisplayName = async (displayName: string) => {
    if (!user || !profile) return false;

    const oldName = profile.display_name;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ display_name: displayName })
        .eq('user_id', user.id);

      if (error) {
        console.error('Error updating display name:', error);
        return false;
      }

      // Update saved groups to replace old name with new name
      if (oldName) {
        const { data: groups } = await supabase
          .from('golf_groups')
          .select('*')
          .eq('user_id', user.id);

        if (groups) {
          for (const group of groups) {
            const updatedNames = group.player_names.map((name: string) => 
              name === oldName ? displayName : name
            );
            
            if (JSON.stringify(updatedNames) !== JSON.stringify(group.player_names)) {
              await supabase
                .from('golf_groups')
                .update({ player_names: updatedNames })
                .eq('id', group.id);
            }
          }
        }
      }

      setProfile(prev => prev ? { ...prev, display_name: displayName } : null);
      return true;
    } catch (err) {
      console.error('Error updating display name:', err);
      return false;
    }
  };

  const updateUsername = async (username: string): Promise<{ success: boolean; error?: string }> => {
    if (!user || !profile) return { success: false, error: 'Not authenticated' };

    // Validate username format
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
    if (!usernameRegex.test(username)) {
      return { 
        success: false, 
        error: 'Username must be 3-20 characters and contain only letters, numbers, and underscores' 
      };
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ username: username.toLowerCase() })
        .eq('user_id', user.id);

      if (error) {
        if (error.code === '23505') {
          return { success: false, error: 'Username is already taken' };
        }
        console.error('Error updating username:', error);
        return { success: false, error: 'Failed to update username' };
      }

      setProfile(prev => prev ? { ...prev, username: username.toLowerCase() } : null);
      return { success: true };
    } catch (err) {
      console.error('Error updating username:', err);
      return { success: false, error: 'Failed to update username' };
    }
  };

  const updateShowEarnings = async (show: boolean): Promise<boolean> => {
    if (!user || !profile) return false;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ show_earnings_to_friends: show })
        .eq('user_id', user.id);

      if (error) {
        console.error('Error updating earnings visibility:', error);
        return false;
      }

      setProfile(prev => prev ? { ...prev, show_earnings_to_friends: show } : null);
      return true;
    } catch (err) {
      console.error('Error updating earnings visibility:', err);
      return false;
    }
  };

  // Get the display name - prioritize profile's display_name, then email username as fallback
  const displayName = profile?.display_name || (profile ? user?.email?.split('@')[0] : null) || null;

  return {
    profile,
    displayName,
    username: profile?.username || null,
    showEarningsToFriends: profile?.show_earnings_to_friends ?? true,
    loading,
    updateDisplayName,
    updateUsername,
    updateShowEarnings,
    isProfileSet: !!profile?.display_name,
    isUsernameSet: !!profile?.username,
    refetch: fetchProfile,
  };
};
